'use client';

import { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/effect-coverflow';

import SidebarTabs from './SidebarTabs';
import Link from 'next/link';

type TabType = 'grid' | 'list' | 'detail' | 'video';

type GridViewProps = {
  setActiveTab: React.Dispatch<React.SetStateAction<TabType>>;
  project: {
    name: string;
    [key: string]: any;
  };
  imageUrls: string[];
  currentSlide: number;
  nextProjectData?: any;
  setCurrentSlide: (index: number) => void;
};

export default function GridView({
  setActiveTab,
  project,
  imageUrls,
  currentSlide,
  setCurrentSlide,
  nextProjectData,
}: GridViewProps) {
  const totalSlides = imageUrls.length;
  const videoEnable = project?.video?.enable_video_controls === true;

  const [mousePosLeft, setMousePosLeft] = useState({ x: 0, y: 0 });
  const [mousePosRight, setMousePosRight] = useState({ x: 0, y: 0 });

  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);

  const swiperRef = useRef<SwiperType | null>(null);
  const lastProjectLinkRef = useRef<HTMLAnchorElement | null>(null);

  const handleMouseMoveLeft = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosLeft({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseMoveRight = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosRight({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <section className="relative min-h-screen">
      <div className="grid grid-cols-12 px-4 relative gap-2 lg:gap-5 py-5">
        <div className="col-span-12 lg:col-span-3 xl:col-span-2 z-10 grid-mainview">
          <SidebarTabs
            setActiveTab={setActiveTab}
            activeTab="grid"
            projectName=""
            position=""
            currentSlide={currentSlide}
            setCurrentSlide={setCurrentSlide}
            totalSlides={totalSlides}
            videoControlsEnabled={!!videoEnable}
          />
        </div>

        <div className="col-span-12 lg:col-span-9 xl:col-span-10 px-4 md:px-0 pt-3 lg:pt-0 grid-slider">
          {/* Left hover area */}
          <div
            className="absolute h-[65vh] w-[30%] left-0 z-50"
            onMouseMove={handleMouseMoveLeft}
            onMouseEnter={() => setShowLeftButton(true)}
            onMouseLeave={() => setShowLeftButton(false)}
          >
            {showLeftButton && (
              <button
                onClick={() => swiperRef.current?.slidePrev()}
                className="text-[#2B332A] text-nowrap text-[12px] uppercase font-dm-mono absolute tracking-widest transition-opacity duration-300 items-center pointer-events-auto z-10 hidden md:flex"
                style={{
                  left: `${mousePosLeft.x}px`,
                  top: `${mousePosLeft.y}px`,
                  transform: 'translate(-50%, -50%)',
                  transition: 'left 0.03s, top 0.03s',
                }}
              >
                Prev
              </button>
            )}
          </div>

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation, Keyboard]}
            navigation
            speed={1000}
            spaceBetween={15}
            slidesPerView={3}
            centeredSlides
            initialSlide={1}
            centeredSlidesBounds
            keyboard={{ enabled: true }}
            className="custom-swiper"
            onSlideChange={(swiper) => {
              setCurrentSlide(swiper?.realIndex);
            }}
          >
            {/* First slide title */}
            <SwiperSlide className="w-auto ml-0 title-slide">
              <h1 className="text-[22px] font-light px-4 leading-none mt-[5rem]">
                {project?.name}
              </h1>
            </SwiperSlide>

            {/* Image slides */}
            {imageUrls.map((img, index) => (
              <SwiperSlide key={index} className="image-slide">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={img} className="img-slide" alt={`Slide ${index}`} />
              </SwiperSlide>
            ))}

            {/* Last project slide */}
            <SwiperSlide className="w-auto ml-0 last-project">
              <div className="flex justify-end w-full relative z-999">
                {nextProjectData?.slug ? (
                  <Link
                    ref={lastProjectLinkRef}
                    href={`/projects/${nextProjectData.slug}`}
                    className="flex justify-between items-center w-[80%] max-w-[360px]"
                  >
                    <h1 className="text-[20px] font-light leading-none flex flex-col gap-2 ps-8">
                      <span className="text-[16px]">Next</span>
                      {nextProjectData.name}
                    </h1>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={nextProjectData.media?.desktop_media?.[0]?.url}
                      alt="Next Project Preview"
                      className="max-w-[74px] max-h-[107px] h-[100vh] w-full object-cover"
                    />
                  </Link>
                ) : (
                  <div className="flex justify-between items-center w-[80%] max-w-[360px] opacity-50 cursor-not-allowed">
                    <h1 className="text-[20px] font-light leading-none flex flex-col gap-2 ps-8">
                      <span className="text-[16px]">Next</span>
                      No more projects
                    </h1>
                  </div>
                )}
              </div>
            </SwiperSlide>
          </Swiper>

          {/* Right hover area */}
          {nextProjectData?.slug ? (
            <div
              className="absolute h-[65vh] w-[30%] right-0 z-50"
              onMouseMove={handleMouseMoveRight}
              onMouseEnter={() => setShowRightButton(true)}
              onMouseLeave={() => setShowRightButton(false)}
            >
              {showRightButton && (
                <button
                  onClick={() => {
                    if (
                      (swiperRef.current?.slides.length ?? 0) - 2 ===
                      swiperRef.current?.activeIndex
                    ) {                      
                      lastProjectLinkRef.current?.click();
                      return;
                    } else {
                      swiperRef.current?.slideNext();
                    }
                  }}
                  className="text-[#2B332A] text-nowrap text-[12px] uppercase font-dm-mono absolute tracking-widest transition-opacity duration-300 items-center pointer-events-auto z-10 hidden md:flex"
                  style={{
                    left: `${mousePosRight.x}px`,
                    top: `${mousePosRight.y}px`,
                    transform: 'translate(-50%, -50%)',
                    transition: 'left 0.03s, top 0.03s',
                  }}
                >
                  Next
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
